import { useState } from 'react'
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts'
import type { CategorySliceRow } from './calc'

interface Props {
  rows: CategorySliceRow[]
  total: number
}

// Color palette: distinct, accessible (≥3:1 vs zinc-50/950 backgrounds).
// Cycled when there are more slices than colors — small categories
// collapse into "Other" anyway so we rarely hit the cycle.
const PALETTE = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#ca8a04',
  '#9333ea',
  '#0891b2',
  '#db2777',
  '#65a30d',
  '#ea580c',
  '#0284c7',
]

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/**
 * Donut + legend for a single month's spending. Click a slice to
 * spotlight (only that slice opaque). Click again to clear.
 *
 * Slices over the visible-count cap collapse into "Other" so the
 * donut stays readable on small datasets.
 */
export function SpendingDonut({ rows, total }: Props) {
  const visible = collapseSmallSlices(rows, 8)
  const [activeId, setActiveId] = useState<string | null>(null)

  if (visible.length === 0) {
    return (
      <div className="rounded-xl border border-zinc-200 p-10 text-center text-sm text-zinc-500 dark:border-zinc-800">
        No outflows yet this period.
      </div>
    )
  }

  return (
    <div className="grid gap-6 rounded-xl border border-zinc-200 p-4 dark:border-zinc-800 md:grid-cols-[260px_1fr]">
      <div className="relative h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={visible}
              dataKey="total"
              nameKey="categoryName"
              innerRadius={70}
              outerRadius={110}
              paddingAngle={1}
              stroke="none"
              onClick={(slice) =>
                setActiveId((curr) =>
                  curr === (slice.payload as CategorySliceRow).categoryId
                    ? null
                    : ((slice.payload as CategorySliceRow).categoryId ?? null),
                )
              }
            >
              {visible.map((row, i) => (
                <Cell
                  key={row.categoryId ?? `none-${i}`}
                  fill={PALETTE[i % PALETTE.length]}
                  fillOpacity={
                    activeId === null || activeId === row.categoryId ? 1 : 0.3
                  }
                  cursor="pointer"
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [
                typeof value === 'number' ? usd.format(value) : String(value),
                String(name),
              ]}
              contentStyle={{ borderRadius: 8, border: 'none', fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-xs uppercase tracking-wide text-zinc-500">Total</div>
          <div className="text-xl font-semibold tabular-nums">{usd.format(total)}</div>
        </div>
      </div>

      <ul className="space-y-1 text-sm">
        {visible.map((row, i) => (
          <li
            key={row.categoryId ?? `none-${i}`}
            className={`flex items-center justify-between rounded px-2 py-1 ${
              activeId === row.categoryId ? 'bg-zinc-100 dark:bg-zinc-800' : ''
            }`}
          >
            <div className="flex items-center gap-2 truncate">
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-sm"
                style={{ backgroundColor: PALETTE[i % PALETTE.length] }}
                aria-hidden
              />
              <span className="truncate">
                <span className="text-xs text-zinc-500">{row.groupName} / </span>
                {row.categoryName}
              </span>
            </div>
            <div className="ml-3 text-right tabular-nums text-zinc-600 dark:text-zinc-300">
              {usd.format(row.total)}
              <span className="ml-2 text-xs text-zinc-400">{(row.pct * 100).toFixed(0)}%</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function collapseSmallSlices(rows: CategorySliceRow[], maxVisible: number): CategorySliceRow[] {
  if (rows.length <= maxVisible) return rows
  const visible = rows.slice(0, maxVisible - 1)
  const rest = rows.slice(maxVisible - 1)
  const restTotal = rest.reduce((a, b) => a + b.total, 0)
  const restPct = rest.reduce((a, b) => a + b.pct, 0)
  return [
    ...visible,
    {
      categoryId: null,
      categoryName: `Other (${rest.length})`,
      groupName: '—',
      total: restTotal,
      pct: restPct,
    },
  ]
}
